import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { App, AppKey, AppStatus } from '../apps/app/app';
import { AppsService } from '../apps/apps.service';
import { AgentService } from '../core/agent/agent.service';
import { DataStorage } from '../core/data/data-storage';
import { removeFalsyValuesFromObject, convertSnakeCaseToCamelCase } from '../core/helpers/utils';
import { take } from 'rxjs/operators';
import { NgxSmartModalComponent } from 'ngx-smart-modal';
import * as Sentry from '@sentry/angular';
import { CustomErrorHandler } from '../core/error-handler';
import { GuidedSetupStepType } from '../guided-setup/guided-setup.service';

@Component({
  selector: 'rosie-finalize',
  template: `
    <rosie-integration-animation [integrationTitle]="app.name" [integrationType]="'extension'"
                                 *ngIf="app?.isIntegrating" [fullScreen]="false">
    </rosie-integration-animation>

    <ngx-smart-modal #billingPromptModal identifier="billingPromptModal"
        [customClass]="'narrow-modal'" [closable]="false" [dismissable]="false" [escapable]="false">
      <div class="modal-title">{{ 'ACCOUNT.BILLING.POST_INSTALL_APPROVAL_TITLE' | translate }}</div>
      <div class="modal-content">
        <p>{{ 'ACCOUNT.BILLING.POST_INSTALL_APPROVAL_MESSAGE' | translate }}</p>
      </div>
      <div class="modal-action-line with-border">
        <rosie-button [customClass]="'secondary narrow'" (click)="dismissBillingPrompt()"
            [translationKey]="'COMMON.LATER'"></rosie-button>
        <rosie-button [customClass]="'narrow slim'" (click)="goToBilling()"
            [translationKey]="'ACCOUNT.BILLING.GO_TO_BILLING'"></rosie-button>
      </div>
    </ngx-smart-modal>
  `,
  styles: [],
  standalone: false
})
export class FinalizeComponent implements OnInit {
  @ViewChild('billingPromptModal') billingPromptModal: NgxSmartModalComponent;

  app: App;
  navigateToUrl = '/integrations';
  finalizeAppKey: AppKey;
  private returnToGuidedSetup = false;

  constructor(private router: Router, private route: ActivatedRoute, private appsService: AppsService,
    private agentService: AgentService) {
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      if (data?.navigateToUrl) {
        this.navigateToUrl = data.navigateToUrl;
      }
      if (data?.finalizeAppKey) {
        this.finalizeAppKey = data.finalizeAppKey;
      }
    });
    this.route.paramMap
      .subscribe((paramMap) => {
        const finalizingAppKey = this.finalizeAppKey || convertSnakeCaseToCamelCase(paramMap.get('id')) as AppKey;
        this.app = this.appsService.allApps.find((app) => { return app.key === finalizingAppKey });
        if (this.app && !this.app.isIntegrating) {
          this.app.isIntegrating = this.router.url.includes('finalize');
          if (this.app.isIntegrating) {
            this.finalizeIntegration();
          }
        }
      });
  }

  navigateToApp(appKey: AppKey) {
    this.router.navigate([this.navigateToUrl, { id: appKey }]);
  }

  private finalizeIntegration() {
    // Set by the guided setup premium features step when it kicks off a calendar
    // connect — a successful finalize should land the user back on that step.
    // Read-and-clear so an abandoned connect can't redirect a later one.
    const guidedSetupProvider = DataStorage.persistent().get('guidedSetupSchedulingProvider');
    DataStorage.persistent().remove('guidedSetupSchedulingProvider');
    this.returnToGuidedSetup = !!guidedSetupProvider && convertSnakeCaseToCamelCase(guidedSetupProvider) === this.app.key;

    this.route.queryParamMap
      .pipe(take(1))
      .subscribe((map: any) => {
        const params = removeFalsyValuesFromObject(map?.params);

        // Check if there's an error message from the OAuth callback
        if (params['error'] || params['error_message']) {
          const errorMessage = params['error_message'] || params['error'];
          this.handleErrorOnFinalize(errorMessage);
          return;
        }

        let requiredParams = {};
        this.app.requiredParams?.forEach((param) => requiredParams[param] = (params[param]));

        if (this.app.hasExternalFlowWithFormFields) {
          Object.assign(requiredParams, this.getAdditionalParams());
        }

        if (this.app.requiredParams.filter(param => requiredParams[param] === undefined)?.length > 0) {
          this.handleErrorOnFinalize();
          return;
        }

        this.appsService.finalizeIntegration(requiredParams, this.app.id)
          .subscribe(
            (data) => {
              this.app.isIntegrating = false;
              this.app.status = AppStatus.Installed;
              if (this.app.key === AppKey.Shopify && data?.billingTransitionInitiated !== false) {
                this.showBillingPrompt();
              } else if (this.returnToGuidedSetup) {
                this.enableSchedulingAndReturnToSetup();
              } else {
                this.navigateToApp(this.app.key);
              }
            },
            (error) => {
              try {
                this.captureFinalizeError(error);
              } catch {}
              const errorMessage = error?.message || error;
              this.handleErrorOnFinalize(errorMessage);
            });
      });
  }

  // Connecting a calendar from the guided setup premium features card also
  // turns the feature on. The agent refresh chained inside
  // updateAgentConfiguration is what makes the setup page render the
  // just-connected calendar — user.agent was loaded at boot, before the
  // finalize call completed.
  private enableSchedulingAndReturnToSetup(): void {
    const goToPremiumFeatures = () => this.router.navigate(['/setup', { step: GuidedSetupStepType.Customize }]);

    this.agentService.updateAgentConfiguration({ configuration: { hasAppointmentSchedulingEnabled: true } })
      .subscribe(goToPremiumFeatures, goToPremiumFeatures);
  }

  private showBillingPrompt(): void {
    setTimeout(() => this.billingPromptModal.open());
  }

  goToBilling(): void {
    this.billingPromptModal.close();
    this.router.navigate(['/account', { section: 'billing' }]);
  }

  dismissBillingPrompt(): void {
    this.billingPromptModal.close();
    this.navigateToApp(this.app.key);
  }

  private captureFinalizeError(error: any) {
    console.error(`Finalize Error [${this.app?.key}]:`, error);

    if (!CustomErrorHandler.isSentryEnabled) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setContext('app_finalize', {
        appKey: this.app?.key,
        appId: this.app?.id,
        status: error?.status,
        statusText: error?.statusText,
        url: error?.url,
        errorBody: typeof error?.error === 'object'
          ? JSON.stringify(error.error)?.substring(0, 500)
          : error?.error,
        hasCode: !!this.route.snapshot.queryParamMap.get('code'),
      });
      scope.setTag('error_type', 'app_finalize');
      scope.setTag('app_key', this.app?.key);
      scope.setLevel('error');

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(`Finalize error [${this.app?.key}]: ${error?.message || 'Unknown error'}`, 'error');
      }
    });
  }

  private handleErrorOnFinalize(errorMessage?: string) {
    this.app.isIntegrating = false;
    this.app.status = null;

    // Persist the error message if provided from backend
    if (errorMessage) {
      DataStorage.persistent().set(`installError-${this.app.id}`, errorMessage);
    }

    this.navigateToApp(this.app.key);
  }

  private getAdditionalParams() {
    const params = {};
    this.app.setupData.formFields?.forEach((field) => {
      const fieldValue = DataStorage.persistent().get(`field-${this.app.id}-${field.name}`)
        || this.app.setupData.formFields?.find(formField => formField.name === field.name)?.value;
      if (fieldValue) {
        params[`${field.name}`] = fieldValue;
        DataStorage.persistent().remove(`field-${this.app.id}-${field.name}`);
      }
    });
    return params;
  }
}
